import React, { useState, useMemo, useEffect } from "react"
import {
  Card,
  CardBody,
  Table,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
} from "reactstrap"
import { Switch } from "antd"
import { FaEdit, FaPlus } from "react-icons/fa"
import { MdDelete } from "react-icons/md"
import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/loader"
import Swal from "sweetalert2"
import {
  fetchData,
  FetchDeleteData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"
import { usePermissionData } from "AppContext"
import PermittedComponent from "PermittedComponent"
import moment from "moment"

// Document Add/Edit Modal
const DocTypeModal = ({
  open,
  toggle,
  selected,
  setSelected,
  onSubmit,
  categories,
}) => {
  const [errors, setErrors] = useState({})

  useEffect(() => {
    setErrors({})
  }, [open])

  const handleSubmit = () => {
    const errs = {}
    if (!selected?.documentType?.trim())
      errs.documentType = "Document name is required"
    if (!selected?.documentCategoryId)
      errs.documentCategoryId = "Category is required"
    if (Object.keys(errs).length) {
      setErrors(errs)
      return
    }
    onSubmit()
    setErrors({})
  }

  return (
    <Modal isOpen={open} centered size="l" toggle={toggle}>
      <ModalHeader toggle={toggle}>
        {selected?.id ? "Edit Document Type" : "Add Document Type"}
        <div>
          <Button className="modal-btn" onClick={handleSubmit}>
            {selected?.id ? "Update" : "Add"}
          </Button>
        </div>
      </ModalHeader>

      <ModalBody>
        <Form>
          <FormGroup>
            <Label>
              Category <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              value={selected?.documentCategoryId || ""}
              onChange={e => {
                setSelected(prev => ({
                  ...prev,
                  documentCategoryId: e.target.value,
                }))
                setErrors(prev => ({ ...prev, documentCategoryId: "" }))
              }}
              className={`rounded-4 ${
                errors.documentCategoryId ? "is-invalid" : ""
              }`}
            >
              <option value="">Select category</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.documentCategory}
                </option>
              ))}
            </Input>
            {errors.documentCategoryId && (
              <div className="invalid-feedback d-block">
                {errors.documentCategoryId}
              </div>
            )}
          </FormGroup>
          <FormGroup>
            <Label>
              Document Name <span className="text-danger">*</span>
            </Label>
            <Input
              value={selected?.documentType || ""}
              onChange={e => {
                setSelected(prev => ({ ...prev, documentType: e.target.value }))
                setErrors(prev => ({ ...prev, documentType: "" }))
              }}
              placeholder="e.g. GST Certificate"
              className={`rounded-4 ${errors.documentType ? "is-invalid" : ""}`}
            />
            {errors.documentType && (
              <div className="invalid-feedback d-block">
                {errors.documentType}
              </div>
            )}
          </FormGroup>
        </Form>
      </ModalBody>
      {/* <ModalFooter>
        <Button className="btn-blue" onClick={handleSubmit}>
          {selected?.id ? "Update" : "Add"}
        </Button>
        <Button color="secondary" onClick={toggle}>
          Cancel
        </Button>
      </ModalFooter> */}
    </Modal>
  )
}

const DocumentsIndex = () => {
  document.title = "Documents | Siddhi Tax Consultant"

  const { checkAccess } = usePermissionData()

  const [pageSize, setPageSize] = useState(20)
  const [searchString, setSearchString] = useState("")
  const [loading, setLoading] = useState(false)
  const [docTypes, setDocTypes] = useState([])
  const [categories, setCategories] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [selected, setSelected] = useState({})

  useEffect(() => {
    getDocTypes()
    getCategories()
  }, [])

  async function getCategories() {
    const APIdata = await fetchData(setLoading, "document-category")
    if (APIdata?.success)
      setCategories(APIdata?.data.filter(d => d.status == true) || [])
  }

  async function getDocTypes() {
    const APIdata = await fetchData(setLoading, "document-types")
    if (APIdata?.success) {
      setDocTypes(APIdata?.data)
    }
  }

  const filteredData = useMemo(
    () =>
      docTypes.filter(item =>
        item?.documentType?.toLowerCase().includes(searchString.toLowerCase())
      ),
    [docTypes, searchString]
  )

  const [currentPage, setCurrentPage] = useState(1)
  const totalPages = Math.ceil(filteredData.length / pageSize)
  const start = (currentPage - 1) * pageSize

  const paginatedData = useMemo(
    () => filteredData.slice(start, start + pageSize),
    [filteredData, currentPage, pageSize]
  )

  const toggleStatus = async (id, checked) => {
    Swal.fire({
      title: `Are you sure you want to ${
        checked ? "Enable" : "Disable"
      } this Document Type?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, proceed!",
      cancelButtonText: "Cancel",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIdata = await FetchPutData(setLoading, "document-types", id, {
          status: checked,
        })
        if (APIdata?.data?.success) {
          Swal.fire({
            title: `Document Type ${
              checked ? "Enabled" : "Disabled"
            } Successfully.`,
            text: APIdata?.data?.message,
            icon: "success",
          })
          getDocTypes()
        } else {
          Swal.fire({
            title: "Error Updating Document Type.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  const handleSubmit = async () => {
    if (selected?.id) {
      const APIdata = await FetchPutData(
        setLoading,
        "document-types",
        selected?.id,
        selected
      )
      if (APIdata?.data?.success) {
        Swal.fire({
          title: "Document Type Updated Successfully.",
          text: APIdata?.data?.message,
          icon: "success",
        })
        setModalOpen(false)
        getDocTypes()
        setSelected({})
      } else {
        Swal.fire({
          title: "Error Updating Document Type.",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    } else {
      const APIdata = await FetchPostData(
        setLoading,
        "document-types",
        selected
      )
      if (APIdata?.data?.success) {
        Swal.fire({
          title: "Document Type Added Successfully.",
          text: APIdata?.data?.message,
          icon: "success",
        })
        setModalOpen(false)
        setSelected({})
        getDocTypes()
      } else {
        Swal.fire({
          title: "Error Adding Document Type.",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    }
  }

  const handleDelete = doc => {
    Swal.fire({
      title: `Are you sure you want to delete "${doc?.documentType}"?`,
      text: "This will remove the document type permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIdata = await FetchDeleteData(
          setLoading,
          "document-types",
          doc?.id
        )
        if (APIdata?.data?.success) {
          Swal.fire({
            title: "Document Type Deleted Successfully.",
            text: APIdata?.data?.message,
            icon: "success",
          })
          getDocTypes()
        } else {
          Swal.fire({
            title: "Error Deleting Document Type.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  return (
    <PermittedComponent
      isAccess={checkAccess(5, "view") || checkAccess(5, "viewglobal")}
    >
      <div className="page-content">
        {loading && <Loader />}
        <Breadcrumb title="Dashboard" breadcrumbItem="Document Types" />

        <Card className="shadow-lg">
          <CardBody>
            {/* Top controls */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex gap-3">
                <Input
                  type="select"
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value))
                    setCurrentPage(1)
                  }}
                  className="rounded-4 w-auto pe-5"
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Input>

                <Input
                  className="rounded-4"
                  placeholder="Search document type..."
                  value={searchString}
                  onChange={e => setSearchString(e.target.value)}
                />
              </div>

              {checkAccess(5, "add") && (
                <Button
                  className="btn-blue rounded-4 px-3"
                  onClick={() => {
                    setSelected({})
                    setModalOpen(true)
                  }}
                >
                  <FaPlus className="me-2" />
                  Add Document Type
                </Button>
              )}
            </div>

            {/* Table */}
            <Table bordered responsive>
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Document Name</th>
                  <th>Category</th>
                  <th>Created At</th>
                  <th>Last Updated At</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center">
                      No document types found
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((item, index) => (
                    <tr key={item.id}>
                      <td>{start + index + 1}</td>
                      <td className="fw-bold">{item?.documentType}</td>
                      <td className="fw-bold">
                        {item?.documentCategory?.documentCategory || "—"}
                      </td>
                      <td>
                        {moment(item?.createdAt).format(
                          "DD/MMM/YYYY | hh:mm A"
                        )}
                      </td>
                      <td>
                        {moment(item?.updatedAt).format(
                          "DD/MMM/YYYY | hh:mm A"
                        )}
                      </td>
                      <td className="text-center">
                        <Switch
                          disabled={!checkAccess(5, "edit")}
                          checked={item.status}
                          onChange={checked => toggleStatus(item.id, checked)}
                        />
                      </td>
                      <td className="text-center">
                        {checkAccess(5, "edit") && (
                          <Button
                            className="bg-success text-white me-2 border-0 shadow-none"
                            onClick={() => {
                              setSelected({
                                ...item,
                                documentCategoryId:
                                  item.documentCategory?.id || "",
                              })
                              setModalOpen(true)
                            }}
                          >
                            <FaEdit size={16} />
                          </Button>
                        )}
                        {checkAccess(5, "delete") && (
                          <Button
                            className="bg-danger text-white border-0 shadow-none"
                            onClick={() => handleDelete(item)}
                          >
                            <MdDelete size={18} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>

            {/* Pagination */}
            <nav
              aria-label="Page navigation"
              className="d-flex justify-content-between align-items-center mt-3"
            >
              <h5 className="mb-0">
                Showing {start + 1} to{" "}
                {Math.min(start + pageSize, filteredData.length)} of{" "}
                {docTypes.length} entries
              </h5>
              <ul className="pagination mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setCurrentPage(prev => Math.max(prev - 1, 1))
                    }
                  >
                    Previous
                  </button>
                </li>

                {[...Array(totalPages)].map((_, i) => {
                  const pageNum = i + 1
                  return (
                    <li
                      key={pageNum}
                      className={`page-item ${
                        pageNum === currentPage ? "active" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </li>
                  )
                })}

                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setCurrentPage(prev => Math.min(prev + 1, totalPages))
                    }
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </CardBody>
        </Card>

        <DocTypeModal
          open={modalOpen}
          toggle={() => setModalOpen(false)}
          selected={selected}
          setSelected={setSelected}
          onSubmit={handleSubmit}
          categories={categories}
        />
      </div>
    </PermittedComponent>
  )
}

export default DocumentsIndex
